import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {MultiSelect} from "primereact/multiselect";
import {Checkbox} from "primereact/checkbox";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CampaignService} from "../../service/CampaignService";
import {UserService} from "../../service/UserService";
import {usage_types, scrapy_cloud_projects} from "../../utils/Constants";
import {user, isAdmin} from "../../utils/Utils";
import {HelpIcon} from "./OnboardingHelp";
import MarketplaceOperatorSettingsFields from "../Campaign/MarketplaceOperatorSettingsFields";
import {readMarketplaceOperator, toMarketplaceOperatorSetting} from "../Campaign/marketplaceOperatorSettings";
import {autoNewProductAdditionDefault} from "../../utils/autoImport";

const campaignService = new CampaignService();
const userService = new UserService();

const SINGULARITY_TYPES = [
    {value: 'campaign_based', label: 'Campaign Based'},
    {value: 'user_based', label: 'User Based'},
    {value: 'no_singularity', label: 'No Singularity'},
];
const MATCHING_METHODS = [
    {value: 'contain_match', label: 'Contain Match'},
    {value: 'exact_match', label: 'Exact Match'},
];
const IMPORT_DAYS = [
    {value: 0, label: 'Mon'}, {value: 1, label: 'Tue'}, {value: 2, label: 'Wed'}, {value: 3, label: 'Thu'},
    {value: 4, label: 'Fri'}, {value: 5, label: 'Sat'}, {value: 6, label: 'Sun'},
];
// Namespaces a campaign can pin for repricing. Mirrors INTEGRATION_STRATEGIES in
// pricerest product/pricing_suggestion/pricing_suggestion_strategy.py - keep
// both in sync.
const REPRICING_INTEGRATIONS = ['trendyol', 'hepsiburada', 'idefix', 'tsoft', 'ticimax', 'entegra',
    'coddepo', 'shopify', 'shopify_oauth', 'woocommerce', 'maropost', 'aaro', 'ikas', 'nebim'];
const DROP_SHIPPING_COUNTRIES = [
    {value: 'amazon.ca', label: 'Canada'}, {value: 'amazon.de', label: 'Germany'},
    {value: 'amazon.it', label: 'Italy'}, {value: 'amazon.nl', label: 'Netherlands'},
    {value: 'amazon.sg', label: 'Singapore'}, {value: 'amazon.com.tr', label: 'Turkey'},
    {value: 'amazon.co.uk', label: 'United Kingdom'}, {value: 'amazon.ae', label: 'UAE'},
    {value: 'amazon.com/', label: 'USA'},
];

// Label key per flag (most are campaign.*, repricing ones use reprice.*).
const FLAG_LABEL = {
    owner_map: 'campaign.owner_map',
    skip_violations_when_owner_unavailable: 'campaign.skip_violations_when_owner_unavailable',
    stock_including_for_map: 'campaign.stock_including_for_map',
    include_out_of_stock_prices: 'campaign.include_out_of_stock_prices',
    show_price_violation_tab: 'campaign.show_price_violation_tab',
    only_new_violations_notification: 'campaign.only_new_violations_notification',
    violation_strategy_schedule_enabled: 'campaign.violation_strategy_schedule_enabled',
    admin_only: 'campaign.admin_only',
    auto_match_writes: 'campaign.auto_match_writes',
    use_sale_value: 'reprice.sale_value',
    only_decrease_if_sale_value: 'reprice.only_decrease_if_sale_value',
    compare_min_price_with_sale_value: 'reprice.compare_min_price_with_sale_value',
    auto_new_product_addition: 'campaign.auto_new_product_addition',
    variant_import: 'campaign.variant_import',
    xml_source_all_campaigns: 'campaign.xml_source_all_campaigns',
    own_prices_from_xml_feed: 'campaign.own_prices_from_xml_feed',
    hepsiburada_api_usage: 'campaign.hepsiburada_api_usage',
    send_product_data: 'campaign.send_product_data',
    market_place: 'campaign.market_place',
    new_seller_automatically_activated: 'campaign.new_seller_automatically_activated',
    shipping_prices_handled_separately: 'campaign.shipping_prices_handled_separately',
    load_all_reviews_again: 'campaign.load_all_reviews_again',
    match_products_from_catalog: 'campaign.match_products_from_catalog',
    track_delivery_time: 'campaign.track_delivery_time',
    category_price_comparison_base: 'campaign.category_price_comparison_base',
};

const MANUFACTURER_FLAGS = ['owner_map', 'skip_violations_when_owner_unavailable', 'stock_including_for_map',
    'only_new_violations_notification', 'violation_strategy_schedule_enabled'];
const SELLER_FLAGS = ['use_sale_value', 'only_decrease_if_sale_value', 'compare_min_price_with_sale_value',
    'show_price_violation_tab'];
// Advanced flags grouped by theme (shown as labeled sub-blocks, not an accordion).
const ADVANCED_GROUPS = [
    {titleKey: 'onboarding.settings.advImport', flags: ['auto_new_product_addition', 'variant_import', 'xml_source_all_campaigns', 'match_products_from_catalog']},
    {titleKey: 'onboarding.settings.advSeller', flags: ['new_seller_automatically_activated', 'market_place', 'shipping_prices_handled_separately', 'include_out_of_stock_prices', 'track_delivery_time']},
    {titleKey: 'onboarding.settings.advIntegration', flags: ['hepsiburada_api_usage', 'send_product_data', 'load_all_reviews_again']},
];

// Matching profiles an admin can attach to a campaign (none = key absent).
const MATCHING_PROFILES = ['tire'];
// Admin-controlled settings keys (mirrors ADMIN_SETTINGS_KEYS in pricerest
// campaign/admin_only.py, which keeps a customer's save from touching them).
// Only an admin's save writes them; see save().
const ADMIN_MATCHING_KEYS = ['admin_only', 'matching_profile', 'auto_match_writes', 'catalog_source'];

const bool = (v, def) => (v === null || v === undefined ? def : v);



// Module-level so input focus is preserved across re-renders (defining these
// inside the component would remount their inputs on every keystroke).
const Field = ({label, children, full, help}) => (
    <div style={{flex: full ? '1 1 100%' : '1 1 240px', minWidth: full ? '100%' : 220}}>
        <label style={{display: 'flex', alignItems: 'center', gap: 4, fontSize: '.8rem', color: '#6b7280', marginBottom: 4}}>
            {label}
            {help && <HelpIcon text={help}/>}
        </label>
        {children}
    </div>
);

const FlagRow = ({flag, t, checked, onChange}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0'}}>
        <Checkbox inputId={flag} checked={checked} onChange={(e) => onChange(e.checked)}/>
        <label htmlFor={flag} style={{margin: 0}}>{t(FLAG_LABEL[flag])}</label>
        <HelpIcon text={t(`onboarding.settings.help.${flag}`)}
                  onL={t('onboarding.settings.onLabel')} offL={t('onboarding.settings.offLabel')}/>
    </div>
);

const SettingsOnboardingStep = ({t, refresh, embedded}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, campaigns, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [currencies, setCurrencies] = useState([]);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState(() => {
        const s = (selectedCampaign && selectedCampaign.settings) || {};
        const parent = selectedCampaign?.parent_campaign
            || (campaigns || []).find(c => c.id === selectedCampaign?.parent_campaign_id) || null;
        return {
            name: selectedCampaign?.name ?? '',
            is_parent: bool(selectedCampaign?.is_parent, false),
            parent_campaign: parent,
            usage_type: s.usage_type ?? null,
            currency: s.currency ?? null,
            country: s.country ?? '',
            brand: s.brand ?? '',
            seller_code: s.seller_code ?? '',
            website: s.website ?? '',
            matching_method: s.matching_method ?? 'contain_match',
            singularity_type: s.singularity_type ?? 'campaign_based',
            displayed_price_history_days: s.displayed_price_history_days ?? null,
            mrp_coefficient: s.mrp_coefficient ?? null,
            repricing_integration: s.repricing_integration ?? null,
            url_for_map_updating: s.url_for_map_updating ?? '',
            websites_for_importing: s.websites_for_importing ?? '',
            product_importing_days: s.product_importing_days ?? [5],
            project_used_for_product_import: s.project_used_for_product_import ?? scrapy_cloud_projects[0].value,
            drop_shipping_country: s.drop_shipping_country ?? null,
            drop_shipping_commission_rate: s.drop_shipping_commission_rate ?? 0,
            drop_shipping_min_rate: s.drop_shipping_min_rate ?? 0,
            drop_shipping_max_rate: s.drop_shipping_max_rate ?? 0,
            // flags
            owner_map: bool(s.owner_map, false),
            skip_violations_when_owner_unavailable: bool(s.skip_violations_when_owner_unavailable, false),
            stock_including_for_map: bool(s.stock_including_for_map, true),
            include_out_of_stock_prices: bool(s.include_out_of_stock_prices, false),
            show_price_violation_tab: bool(s.show_price_violation_tab, false),
            only_new_violations_notification: bool(s.only_new_violations_notification, false),
            violation_strategy_schedule_enabled: bool(s.violation_strategy_schedule_enabled, false),
            use_sale_value: bool(selectedCampaign && selectedCampaign.use_sale_value, false),
            only_decrease_if_sale_value: bool(s.only_decrease_if_sale_value, false),
            compare_min_price_with_sale_value: bool(s.compare_min_price_with_sale_value, false),
            auto_new_product_addition: bool(s.auto_new_product_addition, autoNewProductAdditionDefault(s)),
            variant_import: bool(s.variant_import, bool(user().settings?.variant_import, false)),
            xml_source_all_campaigns: bool(s.xml_source_all_campaigns, false),
            own_prices_from_xml_feed: bool(s.own_prices_from_xml_feed, false),
            catalog_of_campaign_id: s.catalog_of_campaign_id || null,
            hepsiburada_api_usage: bool(s.hepsiburada_api_usage, false),
            send_product_data: bool(s.send_product_data, false),
            market_place: bool(s.market_place, false),
            new_seller_automatically_activated: bool(s.new_seller_automatically_activated, true),
            shipping_prices_handled_separately: bool(s.shipping_prices_handled_separately, false),
            load_all_reviews_again: bool(s.load_all_reviews_again, false),
            match_products_from_catalog: bool(s.match_products_from_catalog, false),
            track_delivery_time: bool(s.track_delivery_time, false),
            admin_only: bool(s.admin_only, false),
            matching_profile: s.matching_profile || null,
            auto_match_writes: bool(s.auto_match_writes, false),
            catalog_source: {host: s.catalog_source?.host ?? '', year: s.catalog_source?.year ?? ''},
            category_price_comparison_base: bool(s.category_price_comparison_base, false),
            marketplace_operator: readMarketplaceOperator(s),
            supplier_module: {
                is_active: bool(s.supplier_module?.is_active, false),
                margin_rate: s.supplier_module?.margin_rate ?? null,
                seller_count: s.supplier_module?.seller_count ?? null,
                expiration_min_months: s.supplier_module?.expiration_min_months ?? null,
            },
        };
    });

    const set = (key, value) => setForm(prev => ({...prev, [key]: value}));
    const setMarketplaceOperator = (value) => setForm(prev => ({...prev, marketplace_operator: value}));
    const setCatalogSource = (key, value) => setForm(prev => ({...prev, catalog_source: {...prev.catalog_source, [key]: value}}));
    const setSupplierModule = (key, value) => setForm(prev => ({...prev, supplier_module: {...prev.supplier_module, [key]: value}}));

    useEffect(() => {
        userService.getCurrencies().then(data => setCurrencies(data || [])).catch(() => {});
    }, []);

    // Reconcile the parent campaign object from its id once campaigns load.
    useEffect(() => {
        if (form.parent_campaign || !selectedCampaign?.parent_campaign_id) return;
        const parent = (campaigns || []).find(c => c.id === selectedCampaign.parent_campaign_id);
        if (parent) setForm(prev => ({...prev, parent_campaign: parent}));
    }, [campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

    // Settings keys (everything in form except the top-level campaign fields).
    // marketplace_operator is merged separately (see save).
    const TOP_LEVEL = ['name', 'is_parent', 'parent_campaign', 'use_sale_value', 'marketplace_operator'];

    const save = () => {
        if (!form.name || !form.name.trim()) {
            toast.current.show({severity: 'error', summary: t('validations.invalid_name'), detail: ''});
            return;
        }
        setSaving(true);
        const merged = {...(selectedCampaign.settings || {})};
        Object.keys(form).forEach(k => { if (!TOP_LEVEL.includes(k) && !ADMIN_MATCHING_KEYS.includes(k)) merged[k] = form[k]; });
        if (isAdmin()) {
            // Booleans are written only when on or already stored, and the
            // profile / source only when set, so campaigns that never used
            // them do not gain inert keys.
            ['admin_only', 'auto_match_writes'].forEach(k => {
                if (form[k] || merged[k] !== undefined) merged[k] = !!form[k];
            });
            if (form.matching_profile) merged.matching_profile = form.matching_profile;
            else delete merged.matching_profile;
            const host = (form.catalog_source.host || '').trim();
            const year = (form.catalog_source.year || '').trim();
            if (form.catalog_of_campaign_id && (host || year)) merged.catalog_source = {host, year};
            else delete merged.catalog_source;
        }
        // Only write the operator setting when it is on or already stored, so
        // campaigns that never used it do not get an inert key added.
        if (form.marketplace_operator.enabled || merged.marketplace_operator) {
            merged.marketplace_operator = toMarketplaceOperatorSetting(form.marketplace_operator);
        }
        campaignService.editCampaign(campaignId, form.name, form.use_sale_value, form.is_parent, form.parent_campaign, merged).then(() => {
            selectedCampaign.name = form.name;
            selectedCampaign.is_parent = form.is_parent;
            selectedCampaign.parent_campaign = form.parent_campaign;
            selectedCampaign.parent_campaign_id = form.parent_campaign?.id || null;
            selectedCampaign.settings = merged;
            selectedCampaign.use_sale_value = form.use_sale_value;
            if (loadCampaigns) loadCampaigns();
            toast.current.show({severity: 'success', summary: t('onboarding.settings.saved'), detail: t('message_detail.successful')});
            if (refresh) refresh();
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setSaving(false));
    };

    const usageType = form.usage_type;

    // Only namespaces the account actually holds credentials for can be pinned;
    // the backend rejects the rest anyway (see is_integration_available).
    const integrationOptions = REPRICING_INTEGRATIONS
        .filter(key => (user()?.api_integration || {})[key])
        .map(key => ({value: key, label: key}));

    return (
        <div>
            {!embedded && <>
                <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-sliders-h"/> {t('onboarding.step.settings.label', 'Kampanya Ayarları')}
                </h2>
                <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.settings.desc')}</p>
            </>}

            {/* Campaign identity + type */}
            <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 8}}>
                <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionCampaign')}</div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                    <Field label={t('campaign.name')}>
                        <InputText value={form.name} style={{width: '100%'}} onChange={(e) => set('name', e.target.value)}/>
                    </Field>
                    <Field label={t('campaign.usage_type')}>
                        <Dropdown value={usageType} options={usage_types(t)} style={{width: '100%'}}
                                  placeholder={t('campaign.usage_type')}
                                  onChange={(e) => set('usage_type', e.value)}/>
                    </Field>
                </div>
                <small style={{display: 'block', color: '#9ca3af', marginTop: 6}}>{t('onboarding.settings.sectionTypeHint')}</small>
                {isAdmin() && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap'}}>
                        <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
                            <Checkbox inputId="is_parent" checked={!!form.is_parent}
                                      onChange={(e) => setForm(prev => ({...prev, is_parent: e.checked,
                                          parent_campaign: e.checked ? null : prev.parent_campaign}))}/>
                            <label htmlFor="is_parent" style={{margin: 0}}>{t('campaign.is_parent_campaign')}</label>
                            <HelpIcon text={t('onboarding.settings.help.is_parent')}
                                      onL={t('onboarding.settings.onLabel')} offL={t('onboarding.settings.offLabel')}/>
                        </span>
                        <span style={{flex: '1 1 240px', minWidth: 220}}>
                            <Dropdown value={form.parent_campaign} showClear style={{width: '100%'}}
                                      disabled={form.is_parent}
                                      options={(campaigns || []).filter(c => c.is_parent && c.id !== campaignId)}
                                      optionLabel="name" placeholder={t('campaign.parent_campaign')}
                                      onChange={(e) => set('parent_campaign', e.value)}/>
                        </span>
                    </div>
                )}
            </div>

            {/* Basic identity */}
            <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionBasic')}</div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                    <Field label={t('campaign.currency')} help={t('onboarding.settings.help.currency')}>
                        <Dropdown value={form.currency} options={currencies} filter showClear style={{width: '100%'}}
                                  onChange={(e) => set('currency', e.value)}/>
                    </Field>
                    <Field label={t('user.country')} help={t('onboarding.settings.help.country')}>
                        <InputText value={form.country} style={{width: '100%'}} onChange={(e) => set('country', e.target.value)}/>
                    </Field>
                    <Field label={t('product.brand')} help={t('onboarding.settings.help.brand')}>
                        <InputText value={form.brand} style={{width: '100%'}} onChange={(e) => set('brand', e.target.value)}/>
                    </Field>
                    <Field label={t('campaign.seller_code')} help={t('onboarding.settings.help.seller_code')}>
                        <InputText value={form.seller_code} style={{width: '100%'}} onChange={(e) => set('seller_code', e.target.value)}/>
                    </Field>
                    <Field label={t('campaign.website')} help={t('onboarding.settings.help.website')}>
                        <InputText value={form.website} style={{width: '100%'}} onChange={(e) => set('website', e.target.value)}/>
                    </Field>
                </div>
            </div>

            {/* Matching & display */}
            <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionMatch')}</div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                    <Field label={t('campaign.matching_method')} help={t('onboarding.settings.help.matching_method')}>
                        <Dropdown value={form.matching_method} options={MATCHING_METHODS} style={{width: '100%'}}
                                  onChange={(e) => set('matching_method', e.value)}/>
                    </Field>
                    <Field label={t('campaign.singularity_type')} help={t('onboarding.settings.help.singularity_type')}>
                        <Dropdown value={form.singularity_type} options={SINGULARITY_TYPES} style={{width: '100%'}}
                                  onChange={(e) => set('singularity_type', e.value)}/>
                    </Field>
                    <Field label={t('campaign.displayed_price_history_days')} help={t('onboarding.settings.help.displayed_price_history_days')}>
                        <InputNumber value={form.displayed_price_history_days} style={{width: '100%'}}
                                     onValueChange={(e) => set('displayed_price_history_days', e.value)}/>
                    </Field>
                    <Field label={t('campaign.mrp_coefficient')} help={t('onboarding.settings.help.mrp_coefficient')}>
                        <InputNumber value={form.mrp_coefficient} mode="decimal" minFractionDigits={2} style={{width: '100%'}}
                                     onValueChange={(e) => set('mrp_coefficient', e.value)}/>
                    </Field>
                </div>
            </div>

            {/* Supplier module */}
            <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <Checkbox inputId="supplier_module_is_active" checked={!!form.supplier_module.is_active}
                              onChange={(e) => setSupplierModule('is_active', e.checked)}/>
                    <label htmlFor="supplier_module_is_active" style={{margin: 0, fontWeight: 700}}>{t('campaign.supplier_module')}</label>
                </span>
                {form.supplier_module.is_active && (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12}}>
                        <Field label={t('campaign.supplier_margin_rate')}>
                            <InputNumber value={form.supplier_module.margin_rate} mode="decimal" minFractionDigits={2} style={{width: '100%'}}
                                         onValueChange={(e) => setSupplierModule('margin_rate', e.value)}/>
                        </Field>
                        <Field label={t('campaign.supplier_seller_count')}>
                            <InputNumber value={form.supplier_module.seller_count} style={{width: '100%'}}
                                         onValueChange={(e) => setSupplierModule('seller_count', e.value)}/>
                        </Field>
                        <Field label={t('campaign.supplier_expiration_min_months')}>
                            <InputNumber value={form.supplier_module.expiration_min_months} style={{width: '100%'}}
                                         onValueChange={(e) => setSupplierModule('expiration_min_months', e.value)}/>
                        </Field>
                    </div>
                )}
            </div>

            {/* Conditional by usage_type */}
            {usageType === 'manufacturer' && (
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                    <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionMap')}</div>
                    <Field label={t('campaign.url_for_map_updating')} full
                           help={t('onboarding.settings.help.url_for_map_updating')}>
                        <InputText value={form.url_for_map_updating} style={{width: '100%'}}
                                   onChange={(e) => set('url_for_map_updating', e.target.value)}/>
                    </Field>
                    {/* skip_violations_when_owner_unavailable only acts on an owner-price MAP */}
                    <div style={{marginTop: 8}}>{MANUFACTURER_FLAGS.filter(f => f !== 'skip_violations_when_owner_unavailable' || form.owner_map).map(f => <FlagRow key={f} flag={f} t={t} checked={!!form[f]} onChange={(v) => set(f, v)}/>)}</div>
                </div>
            )}

            {usageType === 'seller' && (
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                    <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionReprice')}</div>
                    {SELLER_FLAGS.map(f => <FlagRow key={f} flag={f} t={t} checked={!!form[f]} onChange={(v) => set(f, v)}/>)}
                    {isAdmin() && (
                        <div style={{marginTop: 12}}>
                            <Field label={t('campaign.repricing_integration')}
                                   help={t('onboarding.settings.help.repricing_integration')}>
                                <Dropdown value={form.repricing_integration} options={integrationOptions} showClear
                                          placeholder={t('campaign.repricing_integration_none')}
                                          style={{width: '100%'}}
                                          onChange={(e) => set('repricing_integration', e.value ?? null)}/>
                            </Field>
                        </div>
                    )}
                </div>
            )}

            {usageType === 'drop_shipping' && (
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                    <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.settings.sectionDropShipping')}</div>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 14}}>
                        <Field label={t('campaign.drop_shipping_country')}>
                            <Dropdown value={form.drop_shipping_country} options={DROP_SHIPPING_COUNTRIES} style={{width: '100%'}}
                                      onChange={(e) => set('drop_shipping_country', e.value)}/>
                        </Field>
                        <Field label={t('campaign.drop_shipping_commission_rate')}>
                            <InputNumber value={form.drop_shipping_commission_rate} style={{width: '100%'}}
                                         onValueChange={(e) => set('drop_shipping_commission_rate', e.value)}/>
                        </Field>
                        <Field label={t('campaign.drop_shipping_min_rate')}>
                            <InputNumber value={form.drop_shipping_min_rate} style={{width: '100%'}}
                                         onValueChange={(e) => set('drop_shipping_min_rate', e.value)}/>
                        </Field>
                        <Field label={t('campaign.drop_shipping_max_rate')}>
                            <InputNumber value={form.drop_shipping_max_rate} style={{width: '100%'}}
                                         onValueChange={(e) => set('drop_shipping_max_rate', e.value)}/>
                        </Field>
                    </div>
                </div>
            )}

            {/* Advanced (grouped) */}
            <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', marginTop: 12}}>
                <div style={{fontWeight: 700, marginBottom: 4}}>{t('onboarding.settings.sectionAdvanced')}</div>
                <small style={{display: 'block', color: '#6b7280', marginBottom: 12}}>{t('onboarding.settings.sectionAdvancedHint')}</small>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px 24px'}}>
                    {ADVANCED_GROUPS.map(g => (
                        <div key={g.titleKey} style={{border: '1px solid #f1f5f9', borderRadius: 8, padding: '10px 12px', background: '#fafbfc'}}>
                            <div style={{fontWeight: 600, fontSize: '.85rem', color: '#374151', marginBottom: 4}}>{t(g.titleKey)}</div>
                            {g.flags.map(f => (
                                <React.Fragment key={f}>
                                    <FlagRow flag={f} t={t} checked={!!form[f]} onChange={(v) => set(f, v)}/>
                                    {/* Admin-only: it replaces the campaign's whole price
                                        check with its XML feed, an operator decision. */}
                                    {f === 'xml_source_all_campaigns' && isAdmin() &&
                                        <FlagRow flag="own_prices_from_xml_feed" t={t}
                                                 checked={!!form.own_prices_from_xml_feed}
                                                 onChange={(v) => set('own_prices_from_xml_feed', v)}/>}
                                    {f === 'xml_source_all_campaigns' && isAdmin() &&
                                        <div style={{display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0'}}>
                                            <label style={{margin: 0}}>{t('campaign.catalog_of_campaign_id')}</label>
                                            <Dropdown value={form.catalog_of_campaign_id} showClear style={{minWidth: 240}}
                                                      options={(campaigns || []).filter(c => c.id !== campaignId)}
                                                      optionLabel="name" optionValue="id"
                                                      placeholder={t('campaign.catalog_of_campaign_id_placeholder')}
                                                      onChange={(e) => set('catalog_of_campaign_id', e.value || null)}/>
                                            <HelpIcon text={t('onboarding.settings.help.catalog_of_campaign_id')}
                                                      onL={t('onboarding.settings.onLabel')} offL={t('onboarding.settings.offLabel')}/>
                                        </div>}
                                </React.Fragment>
                            ))}
                            {/* Only meaningful to accounts that have the category price
                                comparison report, so it stays out of everyone else's panel. */}
                            {g.titleKey === 'onboarding.settings.advIntegration'
                                && !!user().settings?.category_price_comparison_enabled &&
                                <FlagRow flag="category_price_comparison_base" t={t}
                                         checked={!!form.category_price_comparison_base}
                                         onChange={(v) => set('category_price_comparison_base', v)}/>}
                            {/* Marketplace operator profile: a seller-pipeline feature. */}
                            {g.titleKey === 'onboarding.settings.advSeller'
                                && (usageType ?? user()?.usage_type ?? 'seller') === 'seller' &&
                                <div style={{display: 'flex', alignItems: 'flex-start', gap: 6}}>
                                    <div style={{flex: 1}}>
                                        <MarketplaceOperatorSettingsFields value={form.marketplace_operator}
                                                                           onChange={setMarketplaceOperator}
                                                                           hasSellerCode={!!(form.seller_code || '').trim()}/>
                                    </div>
                                    <div style={{paddingTop: 6}}>
                                        <HelpIcon text={t('onboarding.settings.help.marketplace_operator')}
                                                  onL={t('onboarding.settings.onLabel')} offL={t('onboarding.settings.offLabel')}/>
                                    </div>
                                </div>}
                            {g.titleKey === 'onboarding.settings.advImport' && <>
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8}}>
                                    <Field label={t('campaign.product_importing_days')} help={t('onboarding.settings.help.product_importing_days')}>
                                        <MultiSelect value={form.product_importing_days} options={IMPORT_DAYS}
                                                     optionLabel="label" optionValue="value" display="chip" style={{width: '100%'}}
                                                     onChange={(e) => set('product_importing_days', e.value || [])}/>
                                    </Field>
                                    <Field label={t('campaign.project_used_for_product_import')} help={t('onboarding.settings.help.project_used_for_product_import')}>
                                        <Dropdown value={form.project_used_for_product_import} options={scrapy_cloud_projects}
                                                  style={{width: '100%'}} onChange={(e) => set('project_used_for_product_import', e.value)}/>
                                    </Field>
                                </div>
                                <Field label={t('campaign.websites_for_importing')} full
                                       help={t('onboarding.settings.help.websites_for_importing')}>
                                    <InputText value={form.websites_for_importing} style={{width: '100%', marginTop: 4}}
                                               onChange={(e) => set('websites_for_importing', e.target.value)}/>
                                </Field>
                            </>}
                        </div>
                    ))}
                </div>

                {/* Admin-only catalogue matching: hide the campaign from the customer,
                    pick a matching profile, let the engine write matches. */}
                {isAdmin() && (
                    <div style={{border: '1px solid #f1f5f9', borderRadius: 8, padding: '10px 12px', background: '#fafbfc', marginTop: 12}}>
                        <div style={{fontWeight: 600, fontSize: '.85rem', color: '#374151', marginBottom: 4}}>{t('campaign.matching_profile')}</div>
                        <FlagRow flag="admin_only" t={t} checked={!!form.admin_only} onChange={(v) => set('admin_only', v)}/>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 4}}>
                            <Field label={t('campaign.matching_profile')} help={t('onboarding.settings.help.matching_profile')}>
                                <Dropdown id="matching_profile" value={form.matching_profile} style={{width: '100%'}}
                                          options={[{value: null, label: t('campaign.matching_profile_none')},
                                              ...MATCHING_PROFILES.map(p => ({value: p, label: t(`campaign.matching_profile_${p}`)}))]}
                                          onChange={(e) => set('matching_profile', e.value || null)}/>
                            </Field>
                        </div>
                        <FlagRow flag="auto_match_writes" t={t} checked={!!form.auto_match_writes}
                                 onChange={(v) => set('auto_match_writes', v)}/>
                        {form.catalog_of_campaign_id && (
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 4}}>
                                <Field label={`${t('campaign.catalog_source')} - ${t('campaign.catalog_source_host')}`}
                                       help={t('onboarding.settings.help.catalog_source')}>
                                    <InputText id="catalog_source_host" value={form.catalog_source.host} style={{width: '100%'}}
                                               onChange={(e) => setCatalogSource('host', e.target.value)}/>
                                </Field>
                                <Field label={`${t('campaign.catalog_source')} - ${t('campaign.catalog_source_year')}`}>
                                    <InputText id="catalog_source_year" value={form.catalog_source.year} style={{width: '100%'}}
                                               onChange={(e) => setCatalogSource('year', e.target.value)}/>
                                </Field>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div style={{marginTop: 16, display: 'flex', justifyContent: 'flex-end'}}>
                <Button label={t('actions.save')} icon="pi pi-save" loading={saving} disabled={saving} onClick={save}/>
            </div>
        </div>
    );
};

export default SettingsOnboardingStep;
